import type {
  FactoryOptionDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineItemPriceOnlyDto
} from '@api/machines'
import {
  calculateSectionTotal,
  displayOldPrice,
  matchOldPriceItem,
  Options
} from '@common/utils'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { BreakdownItem } from '../breakdown-item/BreakdownItem'
import { BreakdownSection } from '../breakdown-section/BreakdownSection'
import type {
  FactoryOptionDtoWithChecked,
  HandleOptionChangeFunctionProps
} from '../types'

export type SetArchivedFits = React.Dispatch<
  React.SetStateAction<FactoryOptionDtoWithChecked[]>
>

export const FactoryFitOptionsSection = ({
  factoryOptionItems,
  handleOptionChange,
  highlightedSection,
  quotationItemOldPrices,
  archivedFactoryFits,
  setArchivedFactoryFits
}: {
  factoryOptionItems: FactoryOptionDto[]
  handleOptionChange: HandleOptionChangeFunctionProps
  highlightedSection: boolean
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  archivedFactoryFits: FactoryOptionDtoWithChecked[]
  setArchivedFactoryFits: (values: FactoryOptionDtoWithChecked[]) => void
}) => {
  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParams,
    specId
  } = useParams()

  const quoteId = quoteIdParams ?? specId

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null)

  // Track previous factoryOptionItems to clear loading when mutation completes
  const prevFactoryOptionsRef = useRef(factoryOptionItems)
  useEffect(() => {
    if (prevFactoryOptionsRef.current !== factoryOptionItems) {
      prevFactoryOptionsRef.current = factoryOptionItems
      setLoadingItemId(null)
    }
  }, [factoryOptionItems])

  const fitsWithoutArchived = factoryOptionItems.filter(
    item => !item.isArchived
  )
  const factoryFitOptionsIds = fitsWithoutArchived.map(
    factoryOption => factoryOption.id
  )
  const factoryFitOptionsOldPrices =
    quotationItemOldPrices.factoryOptionItems?.filter(factoryOption =>
      factoryFitOptionsIds.includes(factoryOption.id)
    )

  const sectionTotalCurrentPrice = calculateSectionTotal(fitsWithoutArchived)
  const sectionTotalOldPrice = calculateSectionTotal(
    factoryFitOptionsOldPrices ?? []
  )

  const setUncheckedFactoryFits = (id?: number) => {
    const updatedValues = (archivedFactoryFits ?? []).map(element => {
      if (element.id === id) {
        return {
          ...element,
          checked: false
        }
      }
      return element
    })

    setArchivedFactoryFits(updatedValues)
  }

  return fitsWithoutArchived.length > 0 ? (
    <BreakdownSection
      highlightedSection={highlightedSection}
      oldPrice={displayOldPrice(sectionTotalCurrentPrice, sectionTotalOldPrice)}
      price={sectionTotalCurrentPrice}
      title={`${t('factoryFitOptions')} (${fitsWithoutArchived.length})`}
    >
      {fitsWithoutArchived.map((item: FactoryOptionDto) => (
        <BreakdownItem
          oldPrice={matchOldPriceItem(quotationItemOldPrices, {
            id: item.id ?? 0,
            price: item.dnp ?? 0,
            type: 'factoryOptionItems'
          })}
          onRemove={() => {
            if (item.id) {
              setLoadingItemId(item.id)
              if (attachmentId && equipmentId) {
                handleOptionChange({
                  machineId: equipmentId,
                  option: Options.REMOVE_FACTORY_FIT_WITHIN_MACHINE,
                  optionId: item.id,
                  quotationId: quoteId as string
                })
                setUncheckedFactoryFits(item.id)
                return
              }

              handleOptionChange({
                option: Options.REMOVE_FACTORY_FIT,
                optionId: item.id,
                quotationId: quoteId as string
              })
              setUncheckedFactoryFits(item.id)
            }
          }}
          key={`spec_breakdown_factory_fit_${item.id}`}
          loading={loadingItemId === item.id}
          name={item.name as string}
          price={item.dnp ?? 0}
          removable={true}
        />
      ))}
    </BreakdownSection>
  ) : null
}
